<template>
  <v-container fluid class="pa-5 pb-16 elevation-1">
    <v-sheet class="pa-4 w-100" color="#194c9b" elevation="2" rounded>
      <h1 class="text-h4 font-weight-bold d-flex align-center">
        <v-btn icon color="blue" variant="text">
          <v-icon>mdi-currency-usd</v-icon>
        </v-btn>
        <span class="ml-2">Límites por Unidad</span>
        <!-- <v-btn
          :loading="loading"
          class="ml-auto"
          color="white"
          variant="tonal"
          prepend-icon="mdi-refresh"
          @click="cargar"
        >
          Actualizar
        </v-btn> -->
      </h1>
    </v-sheet>

    <v-alert v-if="error" type="error" variant="tonal" class="mt-4">
      {{ error }}
    </v-alert>

    <div v-if="loading" class="loader mt-6">
      <v-progress-circular indeterminate color="primary" />
      <div class="mt-2 text-medium-emphasis">Cargando unidades…</div>
    </div>

    <template v-else>
      <v-table v-if="units.length" class="mt-4">
        <thead>
          <tr>
            <th>Unidad</th>
            <th>Repartido</th>
            <th>Gastado</th>
            <th>Disponible</th>
            <th>Límite USD</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in paged" :key="u.unidad_id">
            <td>
              <b>{{ u.unidad_nombre }}</b>
            </td>
            <td>{{ money(u.asignado) }}</td>
            <td>{{ money(u.gastado) }}</td>
            <td>{{ money(u.disponible) }}</td>
            <td>
              <input
                type="number"
                min="0"
                step="0.01"
                v-model.number="u.limite_usd"
                class="input-num"
                placeholder="0.00"
              />
            </td>
            <td>
              <button
                class="btn-sm btn-primary"
                :disabled="guardando === u.unidad_id"
                @click="guardar(u)"
              >
                {{ guardando === u.unidad_id ? "..." : "Guardar" }}
              </button>
              <button
                class="btn-sm btn-danger"
                :disabled="Number(u.gastado) === 0 && Number(u.asignado) === 0"
                @click="dialogReset(u)"
              >
                Resetear
              </button>
            </td>
          </tr>
        </tbody>
      </v-table>

      <v-alert v-else type="info" variant="tonal" class="mt-4">
        No hay unidades.
      </v-alert>

      <!-- FOOTER / PAGINACIÓN -->
      <div
        v-if="units.length"
        class="d-flex align-center justify-space-between flex-wrap ga-3 px-4 pb-4 table-footer"
      >
        <div class="d-flex align-center ga-2">
          <span class="text-caption text-medium-emphasis">{{
            $t("paginacion")
          }}</span>
          <v-select
            v-model="itemsPerPage"
            :items="[5, 10, 15, 20]"
            density="compact"
            variant="outlined"
            hide-details
            class="items-select"
            style="max-width: 110px"
          />
        </div>
        <v-pagination
          v-model="page"
          :length="pageCount"
          density="comfortable"
          :total-visible="6"
        />
      </div>
    </template>

    <!-- Confirmación de reset por unidad -->
    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <DeleteInfo
          message="Reinicia el consumo de TODOS los usuarios de la unidad (libera límites y 'gastado'). Se conserva el consumo mensual histórico."
          mainValue="Reset de consumo"
          warningText="Esta acción no se puede deshacer"
          :details="[{ label: 'Unidad', value: unidadReset?.unidad_nombre }]"
        />
        <v-card-actions>
          <v-spacer />
          <v-btn @click="dialog = false">Cancelar</v-btn>
          <v-btn color="red" :loading="reseteando" @click="reset()">
            Resetear
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { calculateUsageUnit, setUnitLimit, resetUnit } from "@/api/usoModel";
import { swal } from "@/utils/swal";
import DeleteInfo from "@/components/delete/DeleteGlobalModal.vue";

const units = ref([]);
const loading = ref(true);
const error = ref("");
const guardando = ref(null); // unidad_id que se está guardando
const itemsPerPage = ref(10);
const page = ref(1);

// Reset de consumo por unidad
const dialog = ref(false);
const reseteando = ref(false);
const unidadReset = ref(null);

function money(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}

async function cargar() {
  loading.value = true;
  error.value = "";
  try {
    const { data } = await calculateUsageUnit();
    units.value = data;
  } catch (e) {
    error.value =
      e.response?.status === 403
        ? "No tienes permisos de administrador"
        : "Error al cargar unidades";
  } finally {
    loading.value = false;
  }
}
onMounted(cargar);

async function guardar(u) {
  guardando.value = u.unidad_id;
  error.value = "";
  try {
    // Campo vacío / no numérico → null (quita el tope)
    const raw = u.limite_usd;
    const limite =
      raw === "" || raw == null || Number.isNaN(raw) ? null : Number(raw);
    await setUnitLimit(u.unidad_id, limite);
    await swal({
      icon: "success",
      title: "Límite guardado",
      timer: 2000,
      showConfirmButton: false,
    });
    await cargar();
  } catch (e) {
    const d = e.response?.data;
    error.value =
      e.response?.status === 400
        ? `${d?.error || "Límite inválido"}${
            d?.asignado != null ? ` (ya repartido: $${d.asignado})` : ""
          }`
        : "No se pudo guardar el límite.";
  } finally {
    guardando.value = null;
  }
}

function dialogReset(u) {
  unidadReset.value = u;
  dialog.value = true;
}

async function reset() {
  if (!unidadReset.value) return;
  reseteando.value = true;
  try {
    const data = await resetUnit(unidadReset.value.unidad_id);
    console.log(data)
    dialog.value = false;
    await swal({
      icon: "success",
      title: "Reset exitoso",
      text: data?.mensaje || "Se reinició el consumo de la unidad.",
      timer: 2500,
      showConfirmButton: false,
    });
    await cargar();
  } catch (e) {
    error.value =
      "Error al resetear: " + (e.response?.data?.error || e.message);
  } finally {
    reseteando.value = false;
  }
}

const pageCount = computed(() =>
  Math.max(1, Math.ceil(units.value.length / (Number(itemsPerPage.value) || 10))),
);
const paged = computed(() => {
  const per = Number(itemsPerPage.value) || 10;
  const start = (page.value - 1) * per;
  return units.value.slice(start, start + per);
});
</script>

<style scoped>
.loader {
  text-align: center;
  padding: 40px;
}

button {
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: white;
  cursor: pointer;
  font-size: 14px;
}
.btn-sm {
  padding: 4px 10px;
  font-size: 13px;
  margin-right: 4px;
}
.btn-primary {
  background: #1e40af;
  color: white;
  border-color: #1e40af;
}
.btn-danger {
  background: #dc2626;
  color: white;
  border-color: #dc2626;
}
button:disabled {
  opacity: 0.6;
  cursor: wait;
}

th,
td {
  padding: 10px 12px;
  text-align: left;
}
th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}

.input-num {
  width: 110px;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}
</style>
